import React from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '@/lib/utils';
import { Toaster } from '@/components/ui/toaster';
import { useLocation } from 'react-router-dom';
import { QuotationPage } from '@/components/quotation/types';

interface AdminLayoutProps {
  children: React.ReactNode;
  // Header props
  title?: string;
  isEditing?: boolean;
  onBack?: () => void;
  onPreview?: () => void;
  onSubmit?: (e?: React.FormEvent) => boolean | void | Promise<boolean>;
  showActions?: boolean;
  isSaving?: boolean;
  // Undo functionality
  canUndo?: boolean;
  onUndo?: () => void;
  // Right sidebar
  showRightSidebar?: boolean;
  // Novas props para cotações
  quotationPages?: QuotationPage[];
  activePage?: string | null;
  setActivePage?: (pageId: string | null) => void;
  addPage?: () => void;
  removePage?: (pageId: string) => void;
  reorderPages?: (sourceIndex: number, destinationIndex: number) => void;
  duplicatePage?: (pageId: string) => void;
  togglePageVisibility?: (pageId: string) => void;
  onSelectQuotationBasicInfo?: () => void;
  useDayMode?: boolean;
  // Novas props para o sistema de publicação
  // Props para copiar página para outra cotação
  copyPageToQuotation?: (pageId: string, targetQuotationId: string) => void;
  currentQuotationId?: string;
  quotationId?: string;
  onPublish?: () => void;
  // Safe navigation - navigation with unsaved changes check
  safeNavigate?: (path: string) => void;
  /** Oculta o menu lateral e expande header/conteúdo (ex.: seleção de hotéis em tela cheia). */
  hideSidebar?: boolean;
}

export function AdminLayout({
  children,
  // Header props
  title,
  isEditing = false,
  onBack,
  onPreview,
  onSubmit,
  showActions = false,
  isSaving,
  // Undo functionality
  canUndo = false,
  onUndo,
  // Right sidebar
  showRightSidebar = true,
  // Novas props para cotações
  quotationPages = [],
  activePage = null,
  setActivePage = () => {},
  addPage,
  removePage,
  reorderPages,
  duplicatePage,
  togglePageVisibility,
  onSelectQuotationBasicInfo,
  useDayMode,
  // Novas props para o sistema de publicação
  quotationId,
  onPublish,
  // Props para copiar página para outra cotação
  copyPageToQuotation,
  currentQuotationId,
  // Safe navigation
  safeNavigate,
  hideSidebar = false,
}: AdminLayoutProps) {
  const location = useLocation();

  const headerHeight = 64;

  // Check if we're in a quotation page
  const isQuotationPage = location.pathname.includes('/admin/quotations/');

  const mainMarginLeft = hideSidebar
    ? 0
    : (isQuotationPage ? '6rem' : '8rem');
  const headerMarginLeft = hideSidebar ? 0 : '16rem';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Left Sidebar */}
      {!hideSidebar && (
        <div className="fixed left-0 top-0 bottom-0 z-50 h-full w-64">
          <Sidebar
            quotationPages={quotationPages}
            activePage={activePage}
            setActivePage={setActivePage}
            addPage={addPage}
            removePage={removePage}
            reorderPages={reorderPages}
            duplicatePage={duplicatePage}
            togglePageVisibility={togglePageVisibility}
            onSelectQuotationBasicInfo={onSelectQuotationBasicInfo}
            useDayMode={useDayMode}
            safeNavigate={safeNavigate}
            copyPageToQuotation={copyPageToQuotation}
            currentQuotationId={currentQuotationId}
          />
        </div>
      )}

      {/* Header - Full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <div style={{ marginLeft: headerMarginLeft, height: `${headerHeight}px` }}>
          <Header
            title={title}
            isEditing={isEditing}
            onBack={onBack}
            onPreview={onPreview}
            onSubmit={onSubmit}
            showActions={showActions}
            isSaving={isSaving}
            canUndo={canUndo}
            onUndo={onUndo}
            quotationId={quotationId}
            onPublish={onPublish}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div
        style={{
          marginLeft: mainMarginLeft,
          marginTop: `${headerHeight}px`,
          marginRight: isQuotationPage ? '-2.2rem' : '0',
        }}
        className="min-h-screen flex flex-col"
      >
        {/* Main Content */}
        <main className={cn(
          'flex-1',
          isQuotationPage ? 'px-8 py-6' : 'px-10 py-6',
        )}
        >
          {children}
        </main>
      </div>

      <Toaster />
    </div>
  );
}
